<template>
  <main class="flex justify-center bg-gray-50">
    <HeaderComponent />
    <section class="w-full pb-[100px] lg:pl-[50px] lg:pt-[95px] pt-[95px] max-h-screen overflow-y-auto relative flex justify-start flex-col lg:p-[25px] p-[20px] h-auto min-h-screen">

      <!-- Notifications -->
      <div class="fixed top-[80px] lg:left-[calc(50%+140px)] left-[50%] z-[100] translate-x-[-50%] w-[90%] max-w-[400px] flex flex-col">
        <div v-if="errorMessage" class="w-full rounded-lg text-white bg-red-500 my-2 font-medium text-center p-4 shadow-lg">{{ errorMessage }}</div>
        <div v-if="successMessage" class="w-full rounded-lg text-white bg-emerald-500 my-2 font-medium text-center p-4 shadow-lg">{{ successMessage }}</div>
      </div>

      <!-- Barre d'en-tête -->
      <div class="w-full mb-6 flex flex-col md:flex-row md:justify-between md:items-center gap-4">
        <div class="flex flex-wrap items-center gap-2 bg-white rounded-lg border border-gray-200 p-1">
          <button @click="setTab('plans')" :class="tab === 'plans' ? 'bg-orange-500 text-white' : 'text-gray-600 hover:bg-gray-100'" class="px-4 py-2 rounded-md text-sm font-semibold transition-colors">Plans</button>
          <button @click="setTab('demandes')" :class="tab === 'demandes' ? 'bg-orange-500 text-white' : 'text-gray-600 hover:bg-gray-100'" class="px-4 py-2 rounded-md text-sm font-semibold transition-colors relative">
            Demandes
            <span v-if="demandesCount" class="absolute -top-1 -right-1 bg-red-600 text-white text-[10px] min-w-[16px] h-[16px] px-1 rounded-full flex items-center justify-center font-bold animate-pulse">{{ demandesCount }}</span>
          </button>
          <button v-if="isSuper" @click="setTab('paiements')" :class="tab === 'paiements' ? 'bg-orange-500 text-white' : 'text-gray-600 hover:bg-gray-100'" class="px-4 py-2 rounded-md text-sm font-semibold transition-colors">
            Paiements <span v-if="versementsPendingCount" class="ml-1 text-xs">({{ versementsPendingCount }} en attente)</span>
          </button>
        </div>
        <div class="flex items-center gap-3 self-start">
          <button v-if="isSuper" @click="openMomoModal" class="px-4 py-2 rounded-lg text-sm font-semibold flex items-center text-white bg-orange-500 hover:bg-orange-600 transition-colors shadow-md">
            <i class="fa-solid fa-gear mr-2"></i> Config MoMo
          </button>
        </div>
      </div>

      <!-- Loader -->
      <div v-if="isLoading" class="text-center h-[300px] w-full flex flex-col justify-center items-center">
        <div class="loader mb-4"></div>
        <p class="text-gray-500 font-medium">Chargement...</p>
      </div>

      <!-- ===================== DEMANDES ===================== -->
      <div v-else-if="tab === 'demandes'">
        <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 mb-4">
          <div class="flex overflow-x-auto bg-white rounded-lg border border-gray-200 p-1">
            <button
              v-for="opt in demandeFilterOptions"
              :key="opt.value"
              @click="demandeFilter = opt.value"
              :class="demandeFilter === opt.value ? 'bg-orange-500 text-white' : 'text-gray-600 hover:bg-gray-100'"
              class="px-4 py-2 rounded-md text-sm font-semibold transition-colors whitespace-nowrap"
            >
              {{ opt.label }}
            </button>
          </div>
          <div class="relative w-full lg:w-[280px]">
            <i class="fa-solid fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
            <input v-model="demandeSearch" @input="debouncedFetchDemandes" type="text" placeholder="Rechercher (client, numéro, produit)" class="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500">
          </div>
        </div>

        <div v-if="filteredDemandes.length === 0" class="text-center w-full h-[300px] flex flex-col justify-center items-center">
          <i class="fa-solid fa-magnifying-glass text-4xl text-gray-300 mb-4"></i>
          <h2 class="text-lg font-semibold text-gray-500">Aucune demande</h2>
        </div>
        <div v-else class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-x-auto">
          <table class="w-full text-sm min-w-[650px]">
            <thead>
              <tr class="text-left text-gray-500 border-b border-gray-100">
                <th class="px-4 py-3 font-semibold">Client</th>
                <th class="px-4 py-3 font-semibold">Produit demandé</th>
                <th class="px-4 py-3 font-semibold">Date</th>
                <th class="px-4 py-3 font-semibold text-center">Statut</th>
                <th class="px-4 py-3 font-semibold text-center">Action</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="plan in filteredDemandes" :key="plan._id" class="border-b border-gray-50 hover:bg-gray-50">
                <td class="px-4 py-3">
                  <p class="font-semibold text-gray-800">{{ plan.client?.nom }}</p>
                  <p class="text-xs text-gray-500">{{ plan.client?.whatsapp }}</p>
                </td>
                <td class="px-4 py-3 text-gray-700">{{ plan.nomDemande }}</td>
                <td class="px-4 py-3 text-gray-500">{{ formatDate(plan.createdAt) }}</td>
                <td class="px-4 py-3 text-center"><span :class="statutClass(plan.statut)" class="text-[11px] px-2 py-1 rounded-full font-semibold">{{ statutLabel(plan.statut) }}</span></td>
                <td class="px-4 py-3 text-center">
                  <button v-if="plan.statut === 'demande'" @click="openSourceModal(plan)" class="text-[11px] px-3 py-1 rounded-full font-semibold bg-orange-100 text-orange-700 hover:bg-orange-200 whitespace-nowrap"><i class="fa-solid fa-box mr-1"></i> Traiter</button>
                  <span v-else class="text-xs text-gray-300">—</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- ===================== PAIEMENTS (super admin) ===================== -->
      <div v-else-if="tab === 'paiements'">
        <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 mb-4">
          <div class="flex overflow-x-auto bg-white rounded-lg border border-gray-200 p-1">
            <button
              v-for="opt in versementFilterOptions"
              :key="opt.value"
              @click="setVersementFilter(opt.value)"
              :class="versementFilter === opt.value ? 'bg-orange-500 text-white' : 'text-gray-600 hover:bg-gray-100'"
              class="px-4 py-2 rounded-md text-sm font-semibold transition-colors whitespace-nowrap"
            >
              {{ opt.label }}
            </button>
          </div>
          <div class="relative w-full lg:w-[280px]">
            <i class="fa-solid fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
            <input v-model="versementSearch" @input="debouncedFetchVersements" type="text" placeholder="Rechercher un client (nom, numéro)" class="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500">
          </div>
        </div>

        <div v-if="versements.length === 0" class="text-center w-full h-[300px] flex flex-col justify-center items-center">
          <i class="fa-solid fa-receipt text-4xl text-gray-300 mb-4"></i>
          <h2 class="text-lg font-semibold text-gray-500">Aucun paiement</h2>
        </div>
        <div v-else class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-x-auto">
          <table class="w-full text-sm min-w-[760px]">
            <thead>
              <tr class="text-left text-gray-500 border-b border-gray-100">
                <th class="px-4 py-3 font-semibold">Client</th>
                <th class="px-4 py-3 font-semibold">Produit</th>
                <th class="px-4 py-3 font-semibold text-right">Envoyé</th>
                <th class="px-4 py-3 font-semibold">Date</th>
                <th class="px-4 py-3 font-semibold text-center">Statut</th>
                <th class="px-4 py-3 font-semibold text-center">Action</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="v in versements" :key="v._id" class="border-b border-gray-50 hover:bg-gray-50">
                <td class="px-4 py-3">
                  <p class="font-semibold text-gray-800">{{ v.plan?.client?.nom }}</p>
                  <p class="text-xs text-gray-500">{{ v.plan?.client?.whatsapp }}</p>
                </td>
                <td class="px-4 py-3 text-gray-700">{{ v.plan?.produit?.nom }}</td>
                <td class="px-4 py-3 text-right font-semibold text-gray-800">{{ formatMoney(v.montantEnvoye || v.montant) }}</td>
                <td class="px-4 py-3 text-gray-500">{{ formatDate(v.createdAt) }}</td>
                <td class="px-4 py-3 text-center"><span :class="versementStatutClass(v.statut)" class="text-[11px] px-2 py-1 rounded-full font-semibold">{{ versementStatutLabel(v.statut) }}</span></td>
                <td class="px-4 py-3 text-center">
                  <div class="flex gap-2 justify-center">
                    <button @click="openVersementModal(v)" class="text-[11px] px-3 py-1 rounded-full font-semibold bg-gray-100 text-gray-700 hover:bg-gray-200 whitespace-nowrap"><i class="fa-solid fa-eye mr-1"></i> Voir</button>
                    <button v-if="v.statut !== 'en_attente'" @click="annulerDecision(v)" :title="`Annuler la décision (${versementStatutLabel(v.statut).toLowerCase()})`" class="text-[11px] px-3 py-1 rounded-full font-semibold bg-amber-50 text-amber-700 hover:bg-amber-100 whitespace-nowrap"><i class="fa-solid fa-rotate-left mr-1"></i> Annuler</button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- ===================== PLANS ===================== -->
      <div v-else-if="tab === 'plans'">
        <div class="flex flex-wrap gap-2 mb-4">
          <select v-model="planFilter" @change="fetchPlans" class="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500">
            <option value="">Tous les statuts</option>
            <option value="a_payer">À payer</option>
            <option value="en_cours">En cours</option>
            <option value="termine">Payé</option>
            <option value="vendu">Vendu</option>
            <option value="annule">Annulé</option>
          </select>
        </div>
        <div v-if="plans.length === 0" class="text-center w-full h-[200px] flex flex-col justify-center items-center">
          <i class="fa-solid fa-list text-4xl text-gray-300 mb-4"></i>
          <h2 class="text-lg font-semibold text-gray-500">Aucun plan</h2>
        </div>
        <div v-else class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-x-auto">
          <table class="w-full text-sm min-w-[800px]">
            <thead>
              <tr class="text-left text-gray-500 border-b border-gray-100">
                <th class="px-4 py-3 font-semibold">Client</th>
                <th class="px-4 py-3 font-semibold">Produit</th>
                <th class="px-4 py-3 font-semibold text-right">Total</th>
                <th class="px-4 py-3 font-semibold text-right">Payé</th>
                <th class="px-4 py-3 font-semibold text-right">Reste</th>
                <th class="px-4 py-3 font-semibold text-center">Statut</th>
                <th class="px-4 py-3 font-semibold text-center">Action</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="plan in plans" :key="plan._id" class="border-b border-gray-50 hover:bg-gray-50">
                <td class="px-4 py-3">
                  <p class="font-semibold text-gray-800">{{ plan.client?.nom }}</p>
                  <p class="text-xs text-gray-500">{{ plan.client?.whatsapp }}</p>
                </td>
                <td class="px-4 py-3 text-gray-700">{{ plan.produit?.nom || plan.nomDemande }}</td>
                <td class="px-4 py-3 text-right text-gray-700">{{ formatMoney(plan.prixTotal) }}</td>
                <td class="px-4 py-3 text-right text-emerald-600">{{ formatMoney(plan.montantPaye) }}</td>
                <td class="px-4 py-3 text-right text-gray-500">{{ formatMoney(Math.max((plan.prixTotal||0)-(plan.montantPaye||0),0)) }}</td>
                <td class="px-4 py-3 text-center"><span :class="statutClass(plan.statut)" class="text-[11px] px-2 py-1 rounded-full font-semibold">{{ statutLabel(plan.statut) }}</span></td>
                <td class="px-4 py-3 text-center">
                  <button v-if="plan.statut === 'termine'" @click="marquerVendu(plan)" class="text-[11px] px-3 py-1 rounded-full font-semibold bg-emerald-100 text-emerald-700 hover:bg-emerald-200 whitespace-nowrap"><i class="fa-solid fa-check mr-1"></i> Marquer vendu</button>
                  <span v-else class="text-xs text-gray-300">—</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- ===================== MODAL SOURCING ===================== -->
      <div v-if="showSourceModal" class="fixed inset-0 bg-black bg-opacity-50 lg:left-[280px] left-0 flex items-center justify-center z-50 p-4">
        <div class="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
          <div class="p-6">
            <div class="flex justify-between items-center mb-4">
              <h3 class="text-xl font-bold text-gray-800">Renseigner le produit</h3>
              <button @click="showSourceModal = false" class="text-gray-400 hover:text-gray-600"><i class="fa-solid fa-times"></i></button>
            </div>
            <form @submit.prevent="submitSource" class="space-y-3">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Nom du produit</label>
                <input v-model="sourceForm.nom" type="text" class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500" required>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Catégorie</label>
                <select v-model="sourceForm.categorie" class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500" required>
                  <option value="iOS">iOS</option>
                  <option value="Android">Android</option>
                </select>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Prix total (FCFA)</label>
                <input v-model="sourceForm.prix" type="number" min="0" class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500" required>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Caractéristiques</label>
                <textarea v-model="sourceForm.caracteristiques" class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 h-[70px] resize-none" placeholder="Capacité, couleur, état..."></textarea>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Photo</label>
                <input @change="onPhotoChange" type="file" accept="image/*" class="w-full text-sm">
              </div>
              <button type="submit" :disabled="isSubmitting" class="w-full bg-orange-500 text-white py-2 rounded-lg font-medium hover:bg-orange-600 flex justify-center items-center">
                <span v-if="isSubmitting" class="loader-small mr-2"></span> Valider et notifier
              </button>
            </form>
          </div>
        </div>
      </div>

      <!-- ===================== MODAL DÉTAIL PAIEMENT ===================== -->
      <div v-if="showVersementModal && versementDetail" class="fixed inset-0 bg-black bg-opacity-50 lg:left-[280px] left-0 flex items-center justify-center z-50 p-4">
        <div class="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
          <a :href="versementDetail.preuve" target="_blank" class="block w-full h-[220px] bg-gray-100 relative">
            <img v-if="versementDetail.preuve" :src="versementDetail.preuve" class="w-full h-full object-cover" alt="preuve">
            <span :class="versementStatutClass(versementDetail.statut)" class="absolute top-3 right-3 text-[11px] px-2 py-1 rounded-full font-semibold">{{ versementStatutLabel(versementDetail.statut) }}</span>
          </a>
          <div class="p-6">
            <div class="flex justify-between items-start mb-4">
              <div>
                <p class="font-bold text-xl text-gray-800">{{ formatMoney(versementDetail.montantEnvoye || versementDetail.montant) }}</p>
                <p class="text-xs text-gray-400">envoyé (à vérifier sur la preuve ci-dessus)</p>
              </div>
              <button @click="showVersementModal = false" class="text-gray-400 hover:text-gray-600"><i class="fa-solid fa-times"></i></button>
            </div>

            <div v-if="versementDetail.montantEnvoye" class="bg-gray-50 rounded-lg p-3 mb-4 text-sm space-y-1">
              <div class="flex justify-between"><span class="text-gray-500">Frais de retrait</span><span class="font-medium text-gray-700">{{ formatMoney(versementDetail.frais) }}</span></div>
              <div class="flex justify-between"><span class="text-gray-500">Crédité sur le plan</span><span class="font-semibold text-emerald-600">{{ formatMoney(versementDetail.montant) }}</span></div>
            </div>

            <div class="space-y-2 text-sm mb-4">
              <div class="flex justify-between py-2 border-b border-gray-50"><span class="text-gray-500">Client</span><span class="font-medium text-gray-800">{{ versementDetail.plan?.client?.nom }}</span></div>
              <div class="flex justify-between py-2 border-b border-gray-50"><span class="text-gray-500">WhatsApp</span><span class="text-gray-700">{{ versementDetail.plan?.client?.whatsapp }}</span></div>
              <div class="flex justify-between py-2 border-b border-gray-50"><span class="text-gray-500">Produit</span><span class="text-gray-700">{{ versementDetail.plan?.produit?.nom }}</span></div>
              <div class="flex justify-between py-2 border-b border-gray-50"><span class="text-gray-500">Date</span><span class="text-gray-700">{{ formatDate(versementDetail.createdAt) }}</span></div>
            </div>

            <p v-if="versementDetail.statut === 'rejete' && versementDetail.motifRejet" class="text-sm text-red-500 mb-4">Motif du rejet : {{ versementDetail.motifRejet }}</p>

            <p v-if="versementDetail.statut === 'en_attente' && versementDetail.annulationDecision?.at" class="text-xs text-gray-500 mb-4">
              Décision précédente ({{ versementStatutLabel(versementDetail.annulationDecision.ancienStatut) }}) annulée par
              {{ versementDetail.annulationDecision.par?.nom }} le {{ formatDate(versementDetail.annulationDecision.at) }}.
            </p>

            <div v-if="versementDetail.statut === 'en_attente'" class="flex gap-2">
              <button @click="validerVersement(versementDetail)" class="flex-1 py-2 rounded-lg text-sm font-semibold bg-emerald-100 text-emerald-700 hover:bg-emerald-200 transition-colors"><i class="fa-solid fa-check mr-1"></i> Valider</button>
              <button @click="openRejetModal(versementDetail)" class="flex-1 py-2 rounded-lg text-sm font-semibold bg-red-50 text-red-600 hover:bg-red-100 transition-colors"><i class="fa-solid fa-xmark mr-1"></i> Rejeter</button>
            </div>
            <button v-else @click="annulerDecision(versementDetail)" class="w-full py-2 rounded-lg text-sm font-semibold bg-amber-50 text-amber-700 hover:bg-amber-100 transition-colors">
              <i class="fa-solid fa-rotate-left mr-1"></i> Annuler la décision ({{ versementStatutLabel(versementDetail.statut).toLowerCase() }})
            </button>
          </div>
        </div>
      </div>

      <!-- ===================== MODAL REJET ===================== -->
      <div v-if="showRejetModal" class="fixed inset-0 bg-black bg-opacity-50 lg:left-[280px] left-0 flex items-center justify-center z-50 p-4">
        <div class="bg-white rounded-xl shadow-xl w-full max-w-md">
          <div class="p-6">
            <div class="flex justify-between items-center mb-4">
              <h3 class="text-xl font-bold text-gray-800">Rejeter le paiement</h3>
              <button @click="showRejetModal = false" class="text-gray-400 hover:text-gray-600"><i class="fa-solid fa-times"></i></button>
            </div>
            <form @submit.prevent="submitRejet" class="space-y-3">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Motif (optionnel)</label>
                <textarea v-model="rejetMotif" class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 h-[80px] resize-none" placeholder="Ex: montant ne correspond pas à la preuve"></textarea>
              </div>
              <button type="submit" :disabled="isSubmitting" class="w-full bg-red-500 text-white py-2 rounded-lg font-medium hover:bg-red-600 flex justify-center items-center">
                <span v-if="isSubmitting" class="loader-small mr-2"></span> Confirmer le rejet
              </button>
            </form>
          </div>
        </div>
      </div>

      <!-- ===================== MODAL CONFIG MOMO ===================== -->
      <div v-if="showMomoModal" class="fixed inset-0 bg-black bg-opacity-50 lg:left-[280px] left-0 flex items-center justify-center z-50 p-4">
        <div class="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
          <div class="p-6">
            <div class="flex justify-between items-center mb-4">
              <h3 class="text-xl font-bold text-gray-800">Configuration MoMo</h3>
              <button @click="showMomoModal = false" class="text-gray-400 hover:text-gray-600"><i class="fa-solid fa-times"></i></button>
            </div>
            <form @submit.prevent="submitMomo" class="space-y-3">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Code USSD (utilisez <code>{montant}</code>)</label>
                <input v-model="momoForm.ussdTemplate" type="text" class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500" placeholder="*880*41*XXXX*{montant}#">
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Instructions</label>
                <textarea v-model="momoForm.instructions" class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 h-[90px] resize-none" placeholder="Étapes de paiement..."></textarea>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Image (optionnelle)</label>
                <input @change="onMomoImageChange" type="file" accept="image/*" class="w-full text-sm">
                <img v-if="momoConfig.image" :src="momoConfig.image" class="mt-2 h-[80px] rounded object-cover" alt="">
              </div>
              <button type="submit" :disabled="isSubmitting" class="w-full bg-orange-500 text-white py-2 rounded-lg font-medium hover:bg-orange-600 flex justify-center items-center">
                <span v-if="isSubmitting" class="loader-small mr-2"></span> Enregistrer
              </button>
            </form>
          </div>
        </div>
      </div>

    </section>
  </main>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import HeaderComponent from '../components/HeaderComponent.vue';
import axios from 'axios';
import { debounce } from 'lodash';
import { useAuthStore } from '@/store/auth';

const authStore = useAuthStore();
const isSuper = computed(() => !!authStore.user?.isSuper);

const tab = ref('plans');
const demandesCount = ref(0);
const versementsPendingCount = ref(0);
const isLoading = ref(false);
const isSubmitting = ref(false);
const errorMessage = ref('');
const successMessage = ref('');

const demandes = ref([]);
const demandeFilter = ref('');
const demandeSearch = ref('');
const demandeFilterOptions = [
  { value: '', label: 'Toutes' },
  { value: 'attente', label: 'En attente' },
  { value: 'traite', label: 'Traitées' }
];
const filteredDemandes = computed(() => {
  if (demandeFilter.value === 'attente') return demandes.value.filter(p => p.statut === 'demande');
  if (demandeFilter.value === 'traite') return demandes.value.filter(p => p.statut !== 'demande');
  return demandes.value;
});
const versements = ref([]);
const versementFilter = ref('');
const versementSearch = ref('');
const versementFilterOptions = [
  { value: '', label: 'Tous' },
  { value: 'en_attente', label: 'En attente' },
  { value: 'valide', label: 'Validés' },
  { value: 'rejete', label: 'Rejetés' }
];
const plans = ref([]);
const planFilter = ref('');

const momoConfig = ref({ ussdTemplate: '', instructions: '', image: '' });

// Modals
const showSourceModal = ref(false);
const sourceForm = ref({ nom: '', categorie: 'iOS', prix: 0, caracteristiques: '' });
const sourcePhoto = ref(null);
const sourcePlanId = ref(null);

const showVersementModal = ref(false);
const versementDetail = ref(null);

const showRejetModal = ref(false);
const rejetMotif = ref('');
const rejetVersement = ref(null);

const showMomoModal = ref(false);
const momoForm = ref({ ussdTemplate: '', instructions: '' });
const momoImage = ref(null);

// Helpers
const formatMoney = (n) => new Intl.NumberFormat('fr-FR').format(Math.round(Number(n) || 0)) + ' F';
const formatDate = (d) => new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
function statutLabel(s) { return { demande: 'En recherche', a_payer: 'À payer', en_cours: 'En cours', termine: 'Payé', vendu: 'Vendu', annule: 'Annulé' }[s] || s; }
function statutClass(s) {
  if (s === 'termine' || s === 'vendu') return 'bg-green-100 text-green-700';
  if (s === 'demande') return 'bg-yellow-100 text-yellow-700';
  if (s === 'annule') return 'bg-red-100 text-red-700';
  return 'bg-orange-100 text-orange-700';
}
function versementStatutLabel(s) { return { en_attente: 'En attente', valide: 'Validé', rejete: 'Rejeté' }[s] || s; }
function versementStatutClass(s) {
  if (s === 'valide') return 'bg-green-100 text-green-700';
  if (s === 'rejete') return 'bg-red-100 text-red-700';
  return 'bg-yellow-100 text-yellow-700';
}
const showSuccess = (m) => { successMessage.value = m; setTimeout(() => (successMessage.value = ''), 5000); };
const showError = (m) => { errorMessage.value = m; setTimeout(() => (errorMessage.value = ''), 8000); };

// Fetch
const fetchDemandes = async () => {
  isLoading.value = true;
  try {
    const params = { origine: 'demande' };
    if (demandeSearch.value) params.search = demandeSearch.value;
    const { data } = await axios.get('/api/admin/plans', { params });
    demandes.value = data || [];
  } catch (e) { showError('Erreur chargement des demandes.'); }
  finally { isLoading.value = false; }
};
const debouncedFetchDemandes = debounce(() => fetchDemandes(), 300);
const fetchVersements = async () => {
  isLoading.value = true;
  try {
    const params = {};
    if (versementFilter.value) params.statut = versementFilter.value;
    if (versementSearch.value) params.search = versementSearch.value;
    const { data } = await axios.get('/api/admin/versements', { params });
    versements.value = data || [];
  } catch (e) { showError('Erreur chargement des paiements.'); }
  finally { isLoading.value = false; }
};
const debouncedFetchVersements = debounce(() => fetchVersements(), 300);
function setVersementFilter(v) { versementFilter.value = v; fetchVersements(); }
const fetchPlans = async () => {
  isLoading.value = true;
  try {
    const { data } = await axios.get('/api/admin/plans', { params: planFilter.value ? { statut: planFilter.value } : {} });
    plans.value = data || [];
  } catch (e) { showError('Erreur chargement des plans.'); }
  finally { isLoading.value = false; }
};
const fetchMomo = async () => {
  try { const { data } = await axios.get('/api/momo-config'); momoConfig.value = data || momoConfig.value; } catch (e) { /* */ }
};
const fetchDemandesCount = async () => {
  try { const { data } = await axios.get('/api/admin/plans/demandes/count'); demandesCount.value = data.count || 0; } catch (e) { /* */ }
};
const fetchVersementsPendingCount = async () => {
  try { const { data } = await axios.get('/api/admin/versements', { params: { statut: 'en_attente' } }); versementsPendingCount.value = (data || []).length; } catch (e) { /* */ }
};

const setTab = (t) => {
  tab.value = t;
  if (t === 'demandes') fetchDemandes();
  else if (t === 'paiements') fetchVersements();
  else if (t === 'plans') fetchPlans();
};

// Sourcing
const openSourceModal = (plan) => {
  sourcePlanId.value = plan._id;
  sourceForm.value = { nom: plan.nomDemande || '', categorie: 'iOS', prix: 0, caracteristiques: '' };
  sourcePhoto.value = null;
  showSourceModal.value = true;
};
const onPhotoChange = (e) => { sourcePhoto.value = e.target.files[0]; };
const submitSource = async () => {
  isSubmitting.value = true;
  try {
    const fd = new FormData();
    fd.append('nom', sourceForm.value.nom);
    fd.append('categorie', sourceForm.value.categorie);
    fd.append('prix', sourceForm.value.prix);
    fd.append('caracteristiques', sourceForm.value.caracteristiques);
    if (sourcePhoto.value) fd.append('photo', sourcePhoto.value);
    await axios.put(`/api/admin/plans/${sourcePlanId.value}/source`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    showSourceModal.value = false;
    showSuccess('Produit renseigné, client notifié.');
    fetchDemandes();
    fetchDemandesCount();
  } catch (e) { showError(e.response?.data?.error || 'Erreur lors du sourcing.'); }
  finally { isSubmitting.value = false; }
};

// Versements
const openVersementModal = (v) => { versementDetail.value = v; showVersementModal.value = true; };
const validerVersement = async (v) => {
  if (!confirm(`Valider le paiement de ${formatMoney(v.montant)} ?`)) return;
  try {
    await axios.put(`/api/admin/versements/${v._id}/valider`);
    showVersementModal.value = false;
    showSuccess('Paiement validé.');
    fetchVersements();
    fetchVersementsPendingCount();
  } catch (e) { showError(e.response?.data?.error || 'Erreur lors de la validation.'); }
};
// Annule une décision (validé/rejeté) et remet le paiement en attente
const annulerDecision = async (v) => {
  const label = v.statut === 'valide' ? 'validation' : 'rejet';
  const extra = v.statut === 'valide' ? `\nLe montant de ${formatMoney(v.montant)} sera retiré du plan du client.` : '';
  if (!confirm(`Annuler la ${label} de ce paiement ? Il repassera en attente de décision.${extra}`)) return;
  try {
    await axios.put(`/api/admin/versements/${v._id}/annuler-decision`);
    showVersementModal.value = false;
    showSuccess('Décision annulée, le paiement est de nouveau en attente.');
    fetchVersements();
    fetchVersementsPendingCount();
    if (plans.value.length) fetchPlans();
  } catch (e) { showError(e.response?.data?.error || 'Erreur lors de l\'annulation de la décision.'); }
};
const openRejetModal = (v) => { rejetVersement.value = v; rejetMotif.value = ''; showVersementModal.value = false; showRejetModal.value = true; };
const submitRejet = async () => {
  isSubmitting.value = true;
  try {
    await axios.put(`/api/admin/versements/${rejetVersement.value._id}/rejeter`, { motif: rejetMotif.value });
    showRejetModal.value = false;
    showSuccess('Paiement rejeté.');
    fetchVersements();
    fetchVersementsPendingCount();
  } catch (e) { showError(e.response?.data?.error || 'Erreur lors du rejet.'); }
  finally { isSubmitting.value = false; }
};

// Plans
const marquerVendu = async (plan) => {
  if (!confirm('Marquer ce plan comme vendu ? Le produit sortira de la vue.')) return;
  try {
    await axios.put(`/api/admin/plans/${plan._id}/vendu`);
    showSuccess('Plan marqué vendu.');
    fetchPlans();
  } catch (e) { showError(e.response?.data?.error || 'Erreur.'); }
};

// MoMo config
const openMomoModal = () => { momoForm.value = { ussdTemplate: momoConfig.value.ussdTemplate, instructions: momoConfig.value.instructions }; momoImage.value = null; showMomoModal.value = true; };
const onMomoImageChange = (e) => { momoImage.value = e.target.files[0]; };
const submitMomo = async () => {
  isSubmitting.value = true;
  try {
    const fd = new FormData();
    fd.append('ussdTemplate', momoForm.value.ussdTemplate || '');
    fd.append('instructions', momoForm.value.instructions || '');
    if (momoImage.value) fd.append('image', momoImage.value);
    await axios.put('/api/momo-config', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    showMomoModal.value = false;
    showSuccess('Configuration enregistrée.');
    fetchMomo();
  } catch (e) { showError(e.response?.data?.error || 'Erreur lors de l\'enregistrement.'); }
  finally { isSubmitting.value = false; }
};

onMounted(() => {
  fetchPlans();
  fetchDemandesCount();
  if (isSuper.value) { fetchMomo(); fetchVersementsPendingCount(); }
});
</script>

<style scoped>
.loader { border: 3px solid #f3f3f3; border-top: 3px solid #f97316; border-radius: 50%; width: 30px; height: 30px; animation: spin 1s linear infinite; }
.loader-small { border: 2px solid #f3f3f3; border-top: 2px solid #fff; border-radius: 50%; width: 16px; height: 16px; animation: spin 1s linear infinite; }
@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
</style>
